import { UI, HStack, Stack, VStack } from '@genial/components';
import { BoxRadius, Color, Spacing } from '@genial/design-tokens';
import { Utils } from '@genial/utils';
import React, { useEffect } from 'react';

import { InputMoney } from '../../../../../../../../../components/InputMoney';
import { OperationType } from '../../../../../../../../../components/OperationType';
import { useFundsSellAssetController } from '../../../../../hooks/products/FundsSell';
import { useFundsSellQuotationController } from '../../../../../hooks/products/FundsSell/quotation';
import { useFundsSellTaxSimulationController } from '../../../../../hooks/products/FundsSell/taxSimulation';
import { LateralBar } from '../../../components/LateralBar';

import { FundsItemSelectedTypes } from '.';

export const FundsItemSelectedTablet: React.FC<FundsItemSelectedTypes.Props> = ({
  offer,
  accountId,
  onUpdate,
}) => {
  const id = offer.productIdNormalized;
  const {
    control,
    handleInputBlur,
    handleInputClear,
    handleInputFocus,
    handleSubmit,
    hasError,
    helpText,
    isFocused,
    currentValueFormatted,
    currentValue,
    handleToggleIsTotalRedemption,
    isTotalRedemption,
    isTotalRedemptionChecked,
  } = useFundsSellAssetController(offer);

  const completeOffer = { ...offer, orderValue: currentValue, isTotalRedemption };

  const { isLoadingTaxes, taxSimulationResponse } = useFundsSellTaxSimulationController({
    valueToSimulate: currentValue,
    product: completeOffer,
  });

  const {
    requestDateErrorMessage,
    onChangeRequestDate,
    onResetRequestDate,
    selectedDate,
    quotationResponse,
    isLoadingDates,
  } = useFundsSellQuotationController({
    isTotalRedemption,
    product: offer,
    accountId,
  });

  useEffect(() => {
    if (offer.orderValue === currentValue || !currentValue) {
      return;
    }
    const updatedOffer = { ...offer, orderValue: currentValue || 0, isTotalRedemption };
    onUpdate(updatedOffer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentValue]);

  useEffect(() => {
    if (offer.scheduleDate === selectedDate || !selectedDate || !quotationResponse) {
      return;
    }
    const updatedOffer = {
      ...offer,
      scheduleDate: selectedDate,
      dateQuotation: quotationResponse,
      isTotalRedemption,
    };
    onUpdate(updatedOffer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate, quotationResponse]);

  return (
    <UI.List testId="selected-product" height="container">
      <LateralBar
        backgroundColor={Color['product.fundos']}
        borderTopLeftRadius={BoxRadius.sm}
        borderBottomLeftRadius={BoxRadius.sm}
      />
      <VStack flex={1}>
        {offer.attentionMessage && (
          <Stack pb="stack.xxxs">
            <UI.Notice
              type="warning"
              backgroundColor={Utils.ColorToken.WithOpacity(
                'feedback.attention.lightest',
                'opacity.medium'
              )}
              textObjects={[
                { text: 'Atenção: ', variant: 'semibold' },
                { text: offer.attentionMessage, variant: 'regular' },
              ]}
            />
          </Stack>
        )}
        <UI.Text pb="stack.xxxs">{offer.productName}</UI.Text>
        <HStack w="full" py="inset.quarck" pb="stack.xxxs">
          <UI.ListItem
            testId="selected-product-category"
            title="Tipo"
            stackProps={{ w: '1/4', flex: 1 }}>
            <OperationType title="Resgate" />
          </UI.ListItem>
          <UI.ListItem
            testId="selected-product-selectTotal"
            title="Resgate Total"
            stackProps={{ w: '1/4', flex: 1 }}>
            <UI.Switch
              onToggle={handleToggleIsTotalRedemption}
              isChecked={isTotalRedemptionChecked}
            />
          </UI.ListItem>
          <UI.ListItem
            testId="selected-product-irEstimated"
            title="IR Estimado"
            stackProps={{ w: '1/4', flex: 1 }}>
            <UI.Text isLoading={isLoadingTaxes}>
              {taxSimulationResponse?.irValueNormalized || offer.estimatedIncomeTax}
            </UI.Text>
          </UI.ListItem>
          <UI.ListItem
            testId="selected-product-estimatedNetValue"
            title="Valor líq. estimado"
            stackProps={{ w: '1/4', flex: 1 }}>
            <UI.Text isLoading={isLoadingTaxes}>
              {taxSimulationResponse?.liquidityValueNormalized || offer.estimatedNetValue}
            </UI.Text>
          </UI.ListItem>
        </HStack>
        <HStack w="full" py="inset.quarck">
          <UI.ListItem
            testId="selected-product-value"
            title="Valor resgate"
            stackProps={{ w: '1/4', flex: 1 }}>
            <Stack pr={Spacing['inline.xxxs']} w="full">
              <InputMoney
                id={`funds-sell-input${id}`}
                name="redemptionValue"
                control={control}
                handleFocus={handleInputFocus}
                handleBlur={handleInputBlur}
                handleSubmit={handleSubmit}
                handleClear={handleInputClear}
                currentValue={currentValueFormatted}
                hasFocus={isFocused}
                minValue={offer.minimumTransaction || 0}
                isDisabled={isTotalRedemptionChecked}
                customInputProps={{
                  ...{ ...(isFocused && hasError && { error: hasError }) },
                  ...{ ...(isFocused && !hasError && { helpText }) },
                }}
              />
            </Stack>
          </UI.ListItem>
          <UI.ListItem
            testId="selected-product-solicitationDate"
            title="Data de solicitação"
            stackProps={{ w: '1/4', flex: 1 }}>
            <Stack pr={Spacing['inline.xxxs']} w="full">
              <UI.DatePicker
                date={selectedDate}
                onSelectDate={onChangeRequestDate}
                onClear={onResetRequestDate}
                availableRange={{ past: new Date(), onlyWeekdays: true }}
                error={requestDateErrorMessage}
                isDisabled={isLoadingDates}
              />
            </Stack>
          </UI.ListItem>
          <UI.ListItem
            testId="selected-product-dateQuotation"
            title="Data de cotização"
            stackProps={{ w: '1/4', flex: 1 }}>
            <Stack minW="80px">
              <UI.Text isLoading={isLoadingDates}>
                {quotationResponse?.quotationDateFormatted}
              </UI.Text>
            </Stack>
          </UI.ListItem>
          <UI.ListItem
            testId="selected-product-liquidationDate"
            title="Data de liquidação"
            stackProps={{ w: '1/4', flex: 1 }}>
            <Stack minW="80px">
              <UI.Text isLoading={isLoadingDates}>
                {quotationResponse?.liquidityDateFormatted}
              </UI.Text>
            </Stack>
          </UI.ListItem>
        </HStack>
      </VStack>
    </UI.List>
  );
};
